console.log("SUPABASE_URL_IN_BUILD =", process.env.REACT_APP_SUPABASE_URL);

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = 'https://yxxaevnnhwvvsggsrvih.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl4eGFldm5uaHd2dnNnZ3NydmloIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE1NjE5MjksImV4cCI6MjA4NzEzNzkyOX0.58_JWkzXHpLX8jqcDN0Wit2tZ9qC5FUWgVQJqsTz5bc'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

const NOTIFICATION_TYPES = {
  NEW_FOLLOWER: 'new_follower',
  CAPSULE_LIKE: 'capsule_like',
  CAPSULE_DISLIKE: 'capsule_dislike',
  NEW_RESPONSE: 'new_response'
}

const PROFILE_EMBED_SELECT = 'display_name, username, avatar_color, avatar_url, depth_score, capsule_count, response_count, interests'
const depthRefreshAttempts = new Map()
const depthRebuildAttempts = new Map()
let attemptedGlobalDepthBackfill = false

const hasDepthEligibleActivity = (profile) =>
  (profile?.capsule_count || 0) > 0 || (profile?.response_count || 0) > 0

const shouldThrottleDepthRebuild = (userId) => {
  if (!userId) return true
  const now = Date.now()
  const last = depthRebuildAttempts.get(userId) || 0
  if (now - last < 5 * 60_000) return true
  depthRebuildAttempts.set(userId, now)
  return false
}

const maybeRebuildUserDepthFromPublishedCapsules = async (userId, capsulesHint = null) => {
  if (!userId || shouldThrottleDepthRebuild(userId)) return false

  try {
    let capsules = capsulesHint
    if (!capsules) {
      const { data, error } = await supabase
        .from('capsules')
        .select('id, depth_feed_score')
        .eq('author_id', userId)
        .eq('is_published', true)
        .lte('publishes_at', new Date().toISOString())
        .limit(50)
      if (error) throw error
      capsules = data || []
    }

    const missingScoreIds = (capsules || [])
      .filter(c => !Number.isFinite(c.depth_feed_score) || c.depth_feed_score <= 0)
      .map(c => c.id)

    if (missingScoreIds.length > 0) {
      await Promise.allSettled(
        missingScoreIds.map((capsuleId) => refreshCapsuleDepthScore(capsuleId))
      )
    }

    await refreshUserDepthScore(userId)
    return true
  } catch (e) {
    console.warn('Depth rebuild fallback failed:', e?.message || e)
    return false
  }
}

const maybeRefreshMissingUserDepthScore = async (profile) => {
  if (!profile?.id) return profile
  if ((profile.depth_score || 0) > 0) return profile
  if (!hasDepthEligibleActivity(profile)) return profile

  const lastAttemptAt = depthRefreshAttempts.get(profile.id) || 0
  const now = Date.now()
  if (now - lastAttemptAt < 60_000) return profile
  depthRefreshAttempts.set(profile.id, now)

  try {
    // Try SQL RPC first (if triggers are deployed)
    await refreshUserDepthScore(profile.id)

    const { data: refreshed, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', profile.id)
      .maybeSingle()

    if (!error && refreshed && (refreshed.depth_score || 0) > 0) return refreshed
  } catch (e) {
    console.warn('SQL RPC failed:', e?.message || e)
  }

  // Fallback: some installs have missing trigger history, so rebuild from published capsules.
  if ((profile?.capsule_count || 0) >= 3) {
    const rebuilt = await maybeRebuildUserDepthFromPublishedCapsules(profile.id)
    if (rebuilt) {
      const { data: rebuiltProfile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profile.id)
        .maybeSingle()
      if (!error && rebuiltProfile && (rebuiltProfile.depth_score || 0) > 0) return rebuiltProfile
    }
  }

  // Last-resort global backfill (one time per app session).
  if (!attemptedGlobalDepthBackfill) {
    attemptedGlobalDepthBackfill = true
    try {
      await supabase.rpc('recompute_all_depth_scores')
      const { data: globallyRefreshed, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profile.id)
        .maybeSingle()
      if (!error && globallyRefreshed) return globallyRefreshed
    } catch (e) {
      console.warn('Global depth backfill unavailable:', e?.message || e)
    }
  }

  return profile
}

// ─── AUTH ────────────────────────────────────────────────────────────────────

export const signUp = async (email, password, username, displayName) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { username, display_name: displayName }
    }
  })
  if (error) throw error

  // Upsert profile row — upsert handles both fresh signups and cases where
  // the row already partially exists (e.g. from a previous failed attempt)
  if (data.user) {
    const { error: profileError } = await supabase.from('profiles').upsert({
      id: data.user.id,
      username,
      display_name: displayName,
      email
    }, { onConflict: 'id' })
    if (profileError) {
      console.error('Profile creation failed:', profileError.message)
      // Don't throw — auth succeeded, profile can be fixed on first save
    }
  }
  return data
}

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session
}

// ─── PROFILES ────────────────────────────────────────────────────────────────

export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  if (error) throw error
  return maybeRefreshMissingUserDepthScore(data)
}

export const updateProfile = async (userId, updates) => {
  // Check if a profile row already exists
  const { data: existing } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .maybeSingle()

  if (existing) {
    // Row exists — just update it (no risk of NOT NULL violations on other columns)
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .maybeSingle()
    if (error) throw error
    return data
  } else {
    // Row missing (signup insert was blocked) — insert fresh with all required fields
    const { data, error } = await supabase
      .from('profiles')
      .insert({ id: userId, ...updates })
      .select()
      .maybeSingle()
    if (error) throw error
    return data
  }
}

// ─── FOLLOWS ─────────────────────────────────────────────────────────────────

export const getIsFollowing = async (followerId, followingId) => {
  if (!followerId || !followingId || followerId === followingId) return false
  const { data, error } = await supabase
    .from('follows')
    .select('id')
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
    .maybeSingle()
  if (error) throw error
  return !!data
}

export const followUser = async (followerId, followingId) => {
  if (!followerId || !followingId || followerId === followingId) return
  const { error } = await supabase
    .from('follows')
    .insert({ follower_id: followerId, following_id: followingId })
  if (error && error.code !== '23505') throw error

  if (!error) {
    await createNotification({
      userId: followingId,
      actorId: followerId,
      type: NOTIFICATION_TYPES.NEW_FOLLOWER
    })
  }
}

export const unfollowUser = async (followerId, followingId) => {
  if (!followerId || !followingId || followerId === followingId) return
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId)
  if (error) throw error
}

export const getFollowCounts = async (userId) => {
  if (!userId) return { followers: 0, following: 0 }

  const [followersRes, followingRes] = await Promise.all([
    supabase
      .from('follows')
      .select('follower_id', { count: 'exact', head: true })
      .eq('following_id', userId),
    supabase
      .from('follows')
      .select('following_id', { count: 'exact', head: true })
      .eq('follower_id', userId)
  ])

  if (followersRes.error) throw followersRes.error
  if (followingRes.error) throw followingRes.error

  return {
    followers: followersRes.count || 0,
    following: followingRes.count || 0
  }
}

// ─── NOTIFICATIONS ───────────────────────────────────────────────────────────

async function createNotification({ userId, actorId = null, type, capsuleId = null, responseId = null, metadata = {} }) {
  if (!userId || !type) return
  if (actorId && userId === actorId) return

  const { error } = await supabase
    .from('notifications')
    .insert({
      user_id: userId,
      actor_id: actorId,
      type,
      capsule_id: capsuleId,
      response_id: responseId,
      metadata,
      is_read: false
    })

  // Don't break the main action if notifications table isn't deployed yet.
  if (error) {
    console.error('Notification insert failed:', error.message)
  }
}

export const getUnreadNotificationCount = async (userId) => {
  if (!userId) return 0
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false)
  if (error) throw error
  return count || 0
}

export const markNotificationsRead = async (userId) => {
  if (!userId) return
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false)
  if (error) throw error
}

export const getNotifications = async (userId, limit = 50) => {
  if (!userId) return []

  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) throw error
  const rows = data || []
  if (rows.length === 0) return []

  const actorIds = [...new Set(rows.map(n => n.actor_id).filter(Boolean))]
  const capsuleIds = [...new Set(rows.map(n => n.capsule_id).filter(Boolean))]

  const [profilesRes, capsulesRes] = await Promise.all([
    actorIds.length
      ? supabase
          .from('profiles')
          .select('id, username, display_name, avatar_color, avatar_url')
          .in('id', actorIds)
      : Promise.resolve({ data: [], error: null }),
    capsuleIds.length
      ? supabase
          .from('capsules')
          .select('id, content')
          .in('id', capsuleIds)
      : Promise.resolve({ data: [], error: null }),
  ])

  if (profilesRes.error) throw profilesRes.error
  if (capsulesRes.error) throw capsulesRes.error

  const profilesById = new Map((profilesRes.data || []).map(p => [p.id, p]))
  const capsulesById = new Map((capsulesRes.data || []).map(c => [c.id, c]))

  return rows.map(n => ({
    ...n,
    actor_profile: n.actor_id ? profilesById.get(n.actor_id) || null : null,
    capsule: n.capsule_id ? capsulesById.get(n.capsule_id) || null : null,
  }))
}

// ─── DIRECT MESSAGES ─────────────────────────────────────────────────────────

function getDmPair(userA, userB) {
  return String(userA) < String(userB)
    ? [userA, userB]
    : [userB, userA]
}

export const getOrCreateDirectConversation = async (userId, otherUserId) => {
  if (!userId || !otherUserId) throw new Error('Missing conversation participants.')
  if (userId === otherUserId) throw new Error('You cannot message yourself.')

  const [userOneId, userTwoId] = getDmPair(userId, otherUserId)

  const { data: existing, error: findError } = await supabase
    .from('direct_conversations')
    .select('*')
    .eq('user_one_id', userOneId)
    .eq('user_two_id', userTwoId)
    .maybeSingle()

  if (findError) throw findError
  if (existing) return existing

  const { data, error } = await supabase
    .from('direct_conversations')
    .insert({
      user_one_id: userOneId,
      user_two_id: userTwoId,
      last_message_at: new Date().toISOString()
    })
    .select('*')
    .single()

  if (error) throw error
  return data
}

export const getDirectConversations = async (userId) => {
  if (!userId) return []

  const { data, error } = await supabase
    .from('direct_conversations')
    .select('*')
    .or(`user_one_id.eq.${userId},user_two_id.eq.${userId}`)
    .order('last_message_at', { ascending: false })
    .limit(100)

  if (error) throw error

  const conversations = data || []
  if (conversations.length === 0) return []

  const otherIds = [...new Set(conversations.map(c => c.user_one_id === userId ? c.user_two_id : c.user_one_id))]
  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, username, display_name, avatar_color, avatar_url')
    .in('id', otherIds)

  if (profilesError) throw profilesError

  const profilesById = new Map((profiles || []).map(p => [p.id, p]))
  return conversations.map(c => {
    const otherUserId = c.user_one_id === userId ? c.user_two_id : c.user_one_id
    return {
      ...c,
      other_user_id: otherUserId,
      other_profile: profilesById.get(otherUserId) || null
    }
  })
}

export const getDirectMessages = async (conversationId) => {
  if (!conversationId) return []
  const { data, error } = await supabase
    .from('direct_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(500)

  if (error) throw error
  return data || []
}

export const sendDirectMessage = async (conversationId, senderId, content) => {
  if (!conversationId || !senderId) throw new Error('Missing message context.')
  const trimmed = content.trim()
  if (!trimmed) throw new Error('Message cannot be empty.')

  const nowIso = new Date().toISOString()
  const { data, error } = await supabase
    .from('direct_messages')
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      content: trimmed
    })
    .select('*')
    .single()

  if (error) throw error

  const { error: updateError } = await supabase
    .from('direct_conversations')
    .update({
      last_message_preview: trimmed.slice(0, 180),
      last_message_sender_id: senderId,
      last_message_at: nowIso,
      updated_at: nowIso
    })
    .eq('id', conversationId)

  if (updateError) throw updateError
  return data
}

// ─── CAPSULES ─────────────────────────────────────────────────────────────────

const CAPSULE_INCUBATION_MINUTES = 30
const RESPONSE_INCUBATION_MINUTES = 20

const getPrimaryInterest = (profile) => {
  if (!profile?.interests || profile.interests.length === 0) return 'General'
  return profile.interests[0]
}

const calculateBridgingScore = (capsule, likesByCapsule, profilesById) => {
  const likes = likesByCapsule.get(capsule.id) || []
  if (likes.length === 0) {
    return {
      score: 0,
      uniqueSupporters: 0,
      distinctClusters: 0,
      crossTribeSupportPct: 0
    }
  }

  const authorProfile = profilesById.get(capsule.author_id) || capsule.profiles || {}
  const authorCluster = getPrimaryInterest(authorProfile)
  const uniqueSupporterIds = [...new Set(likes.map(like => like.user_id))]
  const supporterClusters = uniqueSupporterIds.map((uid) => getPrimaryInterest(profilesById.get(uid)))

  const distinctClusters = new Set(supporterClusters).size
  const crossTribeSupport = supporterClusters.filter(cluster => cluster !== authorCluster).length
  const crossTribeSupportPct = crossTribeSupport / Math.max(1, supporterClusters.length)
  const diversityScore = distinctClusters / Math.max(1, supporterClusters.length)
  const supporterScale = Math.min(1, Math.log2(uniqueSupporterIds.length + 1) / 4)

  const score = Math.round(((diversityScore * 45) + (crossTribeSupportPct * 45) + (supporterScale * 10)) * 10) / 10
  return {
    score,
    uniqueSupporters: uniqueSupporterIds.length,
    distinctClusters,
    crossTribeSupportPct: Math.round(crossTribeSupportPct * 100)
  }
}

const enrichWithBridgingScore = async (capsules = []) => {
  if (capsules.length === 0) return []

  const capsuleIds = capsules.map(c => c.id)
  const authorIds = [...new Set(capsules.map(c => c.author_id).filter(Boolean))]

  let reactions = []
  try {
    const { data: reactionRows, error: reactionsError } = await supabase
      .from('reactions')
      .select('capsule_id, user_id, type')
      .in('capsule_id', capsuleIds)
      .eq('type', 'like')
    if (reactionsError) throw reactionsError
    reactions = reactionRows || []
  } catch (e) {
    console.warn('Bridging score fallback: reactions query unavailable.', e?.message || e)
    return capsules.map(c => ({
      ...c,
      bridging_score: 0,
      bridge_stats: { uniqueSupporters: 0, distinctClusters: 0, crossTribeSupportPct: 0 }
    }))
  }

  const likerIds = [...new Set(reactions.map(r => r.user_id).filter(Boolean))]
  const profileIds = [...new Set([...authorIds, ...likerIds])]
  const profilesById = new Map()

  if (profileIds.length > 0) {
    const { data: profileRows, error: profilesError } = await supabase
      .from('profiles')
      .select('id, interests')
      .in('id', profileIds)

    if (profilesError) {
      console.warn('Bridging score fallback: profile interests unavailable.', profilesError.message)
    } else {
      ;(profileRows || []).forEach((p) => profilesById.set(p.id, p))
    }
  }

  const likesByCapsule = new Map()
  for (const reaction of reactions) {
    if (!likesByCapsule.has(reaction.capsule_id)) likesByCapsule.set(reaction.capsule_id, [])
    likesByCapsule.get(reaction.capsule_id).push(reaction)
  }

  return capsules.map((capsule) => {
    const bridge = calculateBridgingScore(capsule, likesByCapsule, profilesById)
    return {
      ...capsule,
      bridging_score: bridge.score,
      bridge_stats: {
        uniqueSupporters: bridge.uniqueSupporters,
        distinctClusters: bridge.distinctClusters,
        crossTribeSupportPct: bridge.crossTribeSupportPct
      }
    }
  })
}

// Upload an image to Supabase Storage and return its public URL.
// Requires an 'agora-images' bucket set to public in your Supabase project.
export const uploadImage = async (file, userId) => {
  const ext = file.name.split('.').pop().toLowerCase()
  const path = `${userId}/${Date.now()}.${ext}`
  const { error: upErr } = await supabase.storage
    .from('agora-images')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type })
  if (upErr) throw upErr
  const { data: { publicUrl } } = supabase.storage.from('agora-images').getPublicUrl(path)
  return publicUrl
}

export const uploadMultipleImages = async (files, userId) => {
  // Validate input
  if (!files || files.length === 0) return []
  if (files.length > 5) throw new Error('Maximum 5 images allowed per post')

  const MAX_SIZE = 8 * 1024 * 1024 // 8MB
  const VALID_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  const urls = []
  const errors = []

  for (const file of files) {
    try {
      // Validate file
      if (!VALID_TYPES.includes(file.type)) {
        throw new Error(`Invalid file type: ${file.type}. Only JPG, PNG, GIF, WebP allowed.`)
      }
      if (file.size > MAX_SIZE) {
        throw new Error(`File too large: ${file.name}. Max 8MB per image.`)
      }

      // Upload file
      const ext = file.name.split('.').pop().toLowerCase()
      const path = `${userId}/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${ext}`

      const { error: upErr } = await supabase.storage
        .from('agora-images')
        .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type })

      if (upErr) throw upErr

      // Get public URL
      const { data: { publicUrl } } = supabase.storage.from('agora-images').getPublicUrl(path)
      urls.push(publicUrl)
    } catch (err) {
      errors.push({ file: file.name, error: err.message })
    }
  }

  if (errors.length > 0) {
    console.warn('Some images failed to upload:', errors)
    // Continue with successfully uploaded images
  }

  return urls
}

export const uploadProfileImage = async (file, userId) => {
  const ext = file.name.split('.').pop().toLowerCase()
  const path = `avatars/${userId}/${Date.now()}.${ext}`
  const { error: upErr } = await supabase.storage
    .from('agora-images')
    .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type })
  if (upErr) throw upErr
  const { data: { publicUrl } } = supabase.storage.from('agora-images').getPublicUrl(path)
  return publicUrl
}

export const createCapsule = async (userId, content, tags = [], imageUrls = null, sources = null, capsuleType = 'thought') => {
  const now = new Date()
  const publishesAt = new Date(now.getTime() + CAPSULE_INCUBATION_MINUTES * 60 * 1000)

  // Convert single URL to array for backward compatibility
  let imageUrlsArray = null
  if (imageUrls) {
    imageUrlsArray = Array.isArray(imageUrls) ? imageUrls : [imageUrls]
  }

  const { data, error } = await supabase
    .from('capsules')
    .insert({
      author_id: userId,
      content,
      tags,
      image_urls: imageUrlsArray,
      sources: sources,
      capsule_type: capsuleType,
      created_at: now.toISOString(),
      publishes_at: publishesAt.toISOString(),
      is_published: false,
      read_count: 0,
      total_read_seconds: 0,
      response_count: 0
    })
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .single()

  if (error) throw error
  return data
}

export const updateCapsuleContent = async (capsuleId, userId, newContent) => {
  // Snapshot current content into iterations, then apply new content
  // maybeSingle() avoids the "Cannot coerce" PGRST116 error from single()
  const { data: cap, error: fetchErr } = await supabase
    .from('capsules')
    .select('content, iterations')
    .eq('id', capsuleId)
    .eq('author_id', userId)
    .maybeSingle()
  if (fetchErr) throw fetchErr
  if (!cap) throw new Error('Capsule not found or you are not the author')

  const oldEntry = { content: cap.content, updated_at: new Date().toISOString() }
  const iterations = [...(cap.iterations || []), oldEntry]

  const { data, error } = await supabase
    .from('capsules')
    .update({ content: newContent, iterations })
    .eq('id', capsuleId)
    .eq('author_id', userId)
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .maybeSingle()
  if (error) {
    // Likely the iterations column doesn't exist yet in Supabase
    if (error.message?.includes('iterations')) {
      throw new Error('Please add the "iterations" (jsonb, default []) column to your capsules table in Supabase first.')
    }
    throw error
  }
  if (!data) {
    throw new Error('Update blocked — add an UPDATE policy to your capsules table in Supabase (Authentication → Policies).')
  }
  return data
}

export const withdrawCapsule = async (capsuleId, userId) => {
  // Can only withdraw if not yet published
  const { data, error } = await supabase
    .from('capsules')
    .delete()
    .eq('id', capsuleId)
    .eq('author_id', userId)
    .gt('publishes_at', new Date().toISOString()) // Server enforced: only if still in incubation
  if (error) throw error
  return data
}

// Fetch published capsules for the For You feed (bridge-scored first, depth as tie-breaker).
export const getForYouFeed = async (userId, userInterests = [], limit = 20, offset = 0) => {
  const fetchSize = Math.max(limit * 3, limit + offset)
  const { data, error } = await supabase
    .from('capsules')
    .select(`
      *,
      profiles(${PROFILE_EMBED_SELECT}),
      responses(count)
    `)
    .eq('is_published', true)
    .lte('publishes_at', new Date().toISOString())
    .order('depth_feed_score', { ascending: false })
    .range(0, fetchSize - 1)

  if (error) throw error
  const enriched = await enrichWithBridgingScore(data || [])
  const sorted = enriched.sort((a, b) => {
    const bridgeDiff = (b.bridging_score || 0) - (a.bridging_score || 0)
    if (bridgeDiff !== 0) return bridgeDiff
    return (b.depth_feed_score || 0) - (a.depth_feed_score || 0)
  })
  return sorted.slice(offset, offset + limit)
}

// Fetch latest published capsules (chronological)
export const getLatestFeed = async (limit = 20, offset = 0) => {
  const { data, error } = await supabase
    .from('capsules')
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .eq('is_published', true)
    .lte('publishes_at', new Date().toISOString())
    .order('publishes_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (error) throw error
  return data || []
}

// Public incubating capsules for anticipation and discovery.
// Requires an RLS select policy that allows reading capsules during incubation.
export const getPublicIncubatingCapsules = async (limit = 30) => {
  try {
    const { data, error } = await supabase
      .from('capsules')
      .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
      .eq('is_published', false)
      .gt('publishes_at', new Date().toISOString())
      .order('publishes_at', { ascending: true })
      .limit(limit)

    if (error) throw error
    return data || []
  } catch (e) {
    console.warn('Public incubation feed unavailable until policy migration is applied.', e?.message || e)
    return []
  }
}

export const getCapsuleById = async (capsuleId) => {
  const { data, error } = await supabase
    .from('capsules')
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .eq('id', capsuleId)
    .maybeSingle()

  if (error) throw error
  return data
}

export const getIncubationRelatedCapsules = async (capsule, limit = 6) => {
  if (!capsule) return []

  const topic = capsule.tags?.[0] || capsule.content?.split(/\s+/).find(w => w.length > 5) || ''
  if (!topic) return []

  const candidates = await searchCapsules(topic, 'latest', limit + 4)
  return (candidates || [])
    .filter(c => c.id !== capsule.id)
    .slice(0, limit)
}

// Fetch user's pending (in incubation) capsules
export const getPendingCapsules = async (userId) => {
  const { data, error } = await supabase
    .from('capsules')
    .select('*')
    .eq('author_id', userId)
    .eq('is_published', false)
    .gt('publishes_at', new Date().toISOString())
    .order('publishes_at', { ascending: true })

  if (error) throw error
  return data || []
}

// ─── RESPONSES ────────────────────────────────────────────────────────────────

// parentResponseId: pass a response id for nested replies, null for top-level
export const createResponse = async (userId, capsuleId, content, parentResponseId = null, imageUrls = null, sources = null) => {
  const now = new Date()
  const publishesAt = new Date(now.getTime() + RESPONSE_INCUBATION_MINUTES * 60 * 1000)

  // Convert single URL to array for backward compatibility
  let imageUrlsArray = null
  if (imageUrls) {
    imageUrlsArray = Array.isArray(imageUrls) ? imageUrls : [imageUrls]
  }

  const { data, error } = await supabase
    .from('responses')
    .insert({
      author_id: userId,
      capsule_id: capsuleId,
      content,
      image_urls: imageUrlsArray,
      sources: sources,
      parent_response_id: parentResponseId || null,
      created_at: now.toISOString(),
      publishes_at: publishesAt.toISOString(),
      is_published: false
    })
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .single()

  if (error) throw error

  // Persist the count immediately — top-level increments capsule, nested increments parent reply_count
  if (!parentResponseId) {
    await supabase.rpc('increment_response_count', { p_capsule_id: capsuleId })
  } else {
    await supabase.rpc('increment_reply_count', { p_response_id: parentResponseId })
  }

  if (!parentResponseId) {
    const { data: capsuleOwner } = await supabase
      .from('capsules')
      .select('author_id, content')
      .eq('id', capsuleId)
      .maybeSingle()

    if (capsuleOwner?.author_id && capsuleOwner.author_id !== userId) {
      await createNotification({
        userId: capsuleOwner.author_id,
        actorId: userId,
        type: NOTIFICATION_TYPES.NEW_RESPONSE,
        capsuleId,
        responseId: data.id,
        metadata: {
          response_preview: content.slice(0, 180),
          capsule_preview: capsuleOwner.content?.slice(0, 180) || null
        }
      })
    }

    // Update depth scores (non-blocking)
    maybeUpdateUserDepthScore(capsuleOwner?.author_id)
    maybeUpdateUserDepthScore(userId)
  }

  return data
}

export const getResponses = async (capsuleId, userId = null) => {
  const now = new Date().toISOString()

  // Fetch all responses whose incubation has passed (visible to everyone)
  const { data: published, error: pubError } = await supabase
    .from('responses')
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .eq('capsule_id', capsuleId)
    .is('parent_response_id', null) // top-level only
    .lte('publishes_at', now)
    .order('publishes_at', { ascending: true })
  if (pubError) throw pubError

  // Also fetch current user's own pending (still incubating) responses
  let pending = []
  if (userId) {
    const { data: p, error: pendError } = await supabase
      .from('responses')
      .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
      .eq('capsule_id', capsuleId)
      .eq('author_id', userId)
      .is('parent_response_id', null) // top-level only
      .gt('publishes_at', now)
      .order('created_at', { ascending: true })
    if (pendError) throw pendError
    pending = p || []
  }

  return [...(published || []), ...pending]
}

export const deleteResponse = async (responseId, userId) => {
  // Fetch first so we can decrement the right counter after delete
  const { data: resp } = await supabase
    .from('responses')
    .select('capsule_id, parent_response_id')
    .eq('id', responseId)
    .maybeSingle()

  const { error } = await supabase
    .from('responses')
    .delete()
    .eq('id', responseId)
    .eq('author_id', userId)
    .gt('publishes_at', new Date().toISOString())
  if (error) throw error

  if (resp) {
    if (!resp.parent_response_id) {
      await supabase.rpc('decrement_response_count', { p_capsule_id: resp.capsule_id })
    } else {
      await supabase.rpc('decrement_reply_count', { p_response_id: resp.parent_response_id })
    }
  }
}

// Fetch nested replies for a published response
export const getResponseReplies = async (responseId, userId = null) => {
  const now = new Date().toISOString()

  // All replies whose incubation has passed
  const { data: published, error: pubError } = await supabase
    .from('responses')
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .eq('parent_response_id', responseId)
    .lte('publishes_at', now)
    .order('publishes_at', { ascending: true })
  if (pubError) throw pubError

  // Current user's own pending replies
  let pending = []
  if (userId) {
    const { data: p, error: pendError } = await supabase
      .from('responses')
      .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
      .eq('parent_response_id', responseId)
      .eq('author_id', userId)
      .gt('publishes_at', now)
      .order('created_at', { ascending: true })
    if (pendError) throw pendError
    pending = p || []
  }

  return [...(published || []), ...pending]
}

// ─── RESPONSE REACTIONS ───────────────────────────────────────────────────────

export const toggleResponseReaction = async (userId, responseId, type = 'like') => {
  const { data: existing } = await supabase
    .from('response_reactions')
    .select('id, type')
    .eq('user_id', userId)
    .eq('response_id', responseId)
    .maybeSingle()

  if (existing) {
    if (existing.type === type) {
      await supabase.from('response_reactions').delete().eq('id', existing.id)
      return { action: 'removed', type }
    } else {
      await supabase.from('response_reactions').update({ type }).eq('id', existing.id)
      return { action: 'switched', from: existing.type, to: type }
    }
  } else {
    await supabase.from('response_reactions').insert({ user_id: userId, response_id: responseId, type })
    return { action: 'added', type }
  }
}

export const getUserResponseReaction = async (userId, responseId) => {
  const { data } = await supabase
    .from('response_reactions')
    .select('type')
    .eq('user_id', userId)
    .eq('response_id', responseId)
    .maybeSingle()
  return data?.type || null
}

export const updateResponse = async (responseId, userId, content) => {
  const { data, error } = await supabase
    .from('responses')
    .update({ content })
    .eq('id', responseId)
    .eq('author_id', userId)
    .gt('publishes_at', new Date().toISOString())
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .single()
  if (error) throw error
  return data
}

// ─── REACTIONS ────────────────────────────────────────────────────────────────

// type: 'like' | 'dislike'
// Returns { action: 'added'|'removed'|'switched', type, from? }
export const toggleReaction = async (userId, capsuleId, type = 'like') => {
  const { data: existing } = await supabase
    .from('reactions')
    .select('id, type')
    .eq('user_id', userId)
    .eq('capsule_id', capsuleId)
    .maybeSingle()

  if (existing) {
    if (existing.type === type) {
      // Same type — remove it
      const { error } = await supabase.from('reactions').delete().eq('id', existing.id)
      if (error) throw error
      return { action: 'removed', type }
    } else {
      // Different type — switch it (like → dislike or vice versa)
      const { error } = await supabase.from('reactions').update({ type }).eq('id', existing.id)
      if (error) throw error

      const { data: capsule } = await supabase
        .from('capsules')
        .select('author_id, content')
        .eq('id', capsuleId)
        .maybeSingle()

      if (capsule?.author_id && capsule.author_id !== userId) {
        await createNotification({
          userId: capsule.author_id,
          actorId: userId,
          type: type === 'like' ? NOTIFICATION_TYPES.CAPSULE_LIKE : NOTIFICATION_TYPES.CAPSULE_DISLIKE,
          capsuleId,
          metadata: { capsule_preview: capsule.content?.slice(0, 180) || null }
        })
      }

      // Update depth scores (non-blocking)
      if (capsule?.author_id) maybeUpdateUserDepthScore(capsule.author_id)

      return { action: 'switched', from: existing.type, to: type }
    }
  } else {
    const { error } = await supabase.from('reactions').insert({ user_id: userId, capsule_id: capsuleId, type })
    if (error) throw error

    const { data: capsule } = await supabase
      .from('capsules')
      .select('author_id, content')
      .eq('id', capsuleId)
      .maybeSingle()

    if (capsule?.author_id && capsule.author_id !== userId) {
      await createNotification({
        userId: capsule.author_id,
        actorId: userId,
        type: type === 'like' ? NOTIFICATION_TYPES.CAPSULE_LIKE : NOTIFICATION_TYPES.CAPSULE_DISLIKE,
        capsuleId,
        metadata: { capsule_preview: capsule.content?.slice(0, 180) || null }
      })
    }

    // Update depth scores (non-blocking)
    if (capsule?.author_id) maybeUpdateUserDepthScore(capsule.author_id)

    return { action: 'added', type }
  }
}

// Get the current user's reaction type for a capsule (null if none)
export const getUserReaction = async (userId, capsuleId) => {
  const { data } = await supabase
    .from('reactions')
    .select('type')
    .eq('user_id', userId)
    .eq('capsule_id', capsuleId)
    .maybeSingle()
  return data?.type || null
}

// ─── READ TRACKING (depth algo) ───────────────────────────────────────────────

export const recordReadTime = async (userId, capsuleId, seconds) => {
  if (seconds < 5) return // ignore bounces
  // Upsert the read event (DB trigger auto-updates capsule read stats + depth scores)
  await supabase.from('read_events').upsert({
    user_id: userId,
    capsule_id: capsuleId,
    read_seconds: seconds,
    recorded_at: new Date().toISOString()
  }, { onConflict: 'user_id,capsule_id' })

  // Update depth scores for capsule author (non-blocking)
  const { data: capsule } = await supabase
    .from('capsules')
    .select('author_id')
    .eq('id', capsuleId)
    .maybeSingle()
    .catch(() => ({ data: null }))

  if (capsule?.author_id) {
    maybeUpdateUserDepthScore(capsule.author_id)
  }
}

// ─── DEPTH SCORE (manual refresh, used as fallback if triggers aren't set up) ─

// Recompute a single capsule's depth score via RPC
export const refreshCapsuleDepthScore = async (capsuleId) => {
  await supabase.rpc('update_capsule_depth_score', { p_capsule_id: capsuleId })
}

// Recompute a user's depth score via RPC (non-blocking, logs errors)
export const refreshUserDepthScore = async (userId) => {
  if (!userId) return
  try {
    await supabase.rpc('update_user_depth_score', { p_user_id: userId })
  } catch (e) {
    // RPC may not exist yet; that's ok
    console.debug('Depth RPC unavailable:', e?.message || e)
  }
}

// Trigger depth score refresh for a user (best-effort, non-blocking)
export const maybeUpdateUserDepthScore = async (userId) => {
  if (!userId) return
  // Fire and forget - don't block the main action
  refreshUserDepthScore(userId).catch(() => {})
}

// Get the top N users by depth score (for leaderboard)
export const getTopDepthUsers = async (limit = 8) => {
  const runQuery = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, display_name, avatar_color, avatar_url, depth_score, capsule_count, response_count')
      .gt('depth_score', 0)
      .order('depth_score', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }

  let users = await runQuery()
  if (users.length > 0) return users

  if (!attemptedGlobalDepthBackfill) {
    attemptedGlobalDepthBackfill = true
    try {
      // One-time opportunistic backfill for installs where the SQL exists but wasn't run yet.
      await supabase.rpc('recompute_all_depth_scores')
      users = await runQuery()
    } catch (e) {
      // Ignore if RPC does not exist or is blocked.
      console.warn('Global depth backfill unavailable:', e?.message || e)
    }
  }

  return users
}

// ─── SEARCH ───────────────────────────────────────────────────────────────────

// Search published capsules by keyword.
// sort: 'depth_desc' | 'depth_asc' | 'latest'
export const searchCapsules = async (query, sort = 'depth_desc', limit = 30) => {
  let q = supabase
    .from('capsules')
    .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
    .eq('is_published', true)
    .lte('publishes_at', new Date().toISOString())
    .ilike('content', `%${query}%`)

  if (sort === 'depth_desc') q = q.order('depth_feed_score', { ascending: false })
  else if (sort === 'depth_asc') q = q.order('depth_feed_score', { ascending: true })
  else q = q.order('publishes_at', { ascending: false }) // 'latest'

  q = q.limit(limit)
  const { data, error } = await q
  if (error) throw error
  return data || []
}

// Search users by username or display_name
export const searchUsers = async (query, limit = 20) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, display_name, bio, avatar_color, avatar_url, depth_score, capsule_count')
    .or(`username.ilike.%${query}%,display_name.ilike.%${query}%`)
    .limit(limit)
  if (error) throw error
  return data || []
}

// Get a user's public profile by username
export const getUserPublicProfile = async (username) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('username', username)
    .maybeSingle()
  if (error) throw error
  if (!data) return null

  // Refresh depth score if missing
  return await maybeRefreshMissingUserDepthScore(data)
}

// Get all published capsules for a public profile
export const getUserPublicCapsules = async (userId, limit = 30) => {
  const runQuery = async () => {
    const { data, error } = await supabase
      .from('capsules')
      .select(`*, profiles(${PROFILE_EMBED_SELECT})`)
      .eq('author_id', userId)
      .eq('is_published', true)
      .lte('publishes_at', new Date().toISOString())
      .order('publishes_at', { ascending: false })
      .limit(limit)
    if (error) throw error
    return data || []
  }

  let rows = await runQuery()
  const hasMissingScores = rows.some(c => !Number.isFinite(c.depth_feed_score) || c.depth_feed_score <= 0)
  if (hasMissingScores) {
    const rebuilt = await maybeRebuildUserDepthFromPublishedCapsules(userId, rows)
    if (rebuilt) {
      rows = await runQuery()
    }
  }

  return rows
}

// Get published responses by a user (for their public profile)
export const getUserPublicResponses = async (userId, limit = 20) => {
  const { data, error } = await supabase
    .from('responses')
    .select(`*, capsules(id, content, publishes_at, author_id)`)
    .eq('author_id', userId)
    .is('parent_response_id', null)
    .lte('publishes_at', new Date().toISOString())
    .order('publishes_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// ─── NEWS ─────────────────────────────────────────────────────────────────────

export const getNewsItems = async (limit = 20) => {
  const { data, error } = await supabase
    .from('news_items')
    .select('*')
    .order('published_at', { ascending: false })
    .limit(limit)

  if (error) throw error
  return data || []
}

export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + "/reset-password"
  });
  if (error) throw error;
}

// ─── ARGUMENT QUALITY VOTING ───────────────────────────────────────────────────

export const voteOnArgumentQuality = async (capsuleId, criterion, score) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Must be logged in to vote')

  const { data, error } = await supabase
    .from('argument_quality_votes')
    .upsert({
      capsule_id: capsuleId,
      voter_id: user.id,
      criterion,
      score
    }, {
      onConflict: 'capsule_id,voter_id,criterion'
    })

  if (error) throw error

  // Trigger depth score update after vote
  await maybeUpdateUserDepthScore(await getCapsuleAuthorId(capsuleId))

  return data
}

export const getArgumentQualityScore = async (capsuleId) => {
  const { data, error } = await supabase
    .from('argument_quality_votes')
    .select('criterion, score')
    .eq('capsule_id', capsuleId)

  if (error) throw error

  // Calculate averages by criterion
  const scores = {
    clarity: { average: 0, count: 0 },
    evidence: { average: 0, count: 0 },
    logic: { average: 0, count: 0 },
    nuance: { average: 0, count: 0 }
  }

  data?.forEach(vote => {
    if (scores[vote.criterion]) {
      scores[vote.criterion].average += vote.score
      scores[vote.criterion].count += 1
    }
  })

  // Calculate averages
  Object.keys(scores).forEach(key => {
    if (scores[key].count > 0) {
      scores[key].average = scores[key].average / scores[key].count
    }
  })

  return scores
}

const getCapsuleAuthorId = async (capsuleId) => {
  const { data, error } = await supabase
    .from('capsules')
    .select('author_id')
    .eq('id', capsuleId)
    .single()

  if (error) throw error
  return data?.author_id
}

// ─── DELETE CAPSULE ───────────────────────────────────────────────────────────

export const deleteCapsule = async (capsuleId) => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Must be logged in to delete')

  // Verify user is the author
  const { data: capsule, error: fetchError } = await supabase
    .from('capsules')
    .select('author_id')
    .eq('id', capsuleId)
    .maybeSingle()

  if (fetchError) throw fetchError
  if (!capsule) throw new Error('Post not found')
  if (capsule.author_id !== user.id) throw new Error('Can only delete your own posts')

  // Delete the capsule
  const { error: deleteError } = await supabase
    .from('capsules')
    .delete()
    .eq('id', capsuleId)

  if (deleteError) throw deleteError
}


// ═══════════════════════════════════════════════════════════
// CIVIC INTELLIGENCE — Client-side helpers
// ═══════════════════════════════════════════════════════════

// Fetch civic events with optional city/topic filters
export async function getCivicEvents({ city, topic, limit = 20, offset = 0 } = {}) {
  let query = supabase
    .from('civic_events')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (city && city !== 'All') query = query.eq('city', city)
  if (topic && topic !== 'All') query = query.eq('topic_tag', topic)

  const { data, error } = await query
  if (error) throw error
  return data || []
}

// Fetch civic events for a user's preferred cities/topics
export async function getCivicEventsForUser(profile, { limit = 20, offset = 0 } = {}) {
  const cities = profile?.civic_cities || []
  const topics = profile?.civic_topics || []

  let query = supabase
    .from('civic_events')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (cities.length) query = query.in('city', cities)
  if (topics.length) query = query.in('topic_tag', topics)

  const { data, error } = await query
  if (error) throw error
  return data || []
}

// Get unread civic notification count for a user
export async function getUnreadCivicNotificationCount(userId) {
  if (!userId) return 0
  const { count, error } = await supabase
    .from('civic_notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false)
  if (error) return 0
  return count || 0
}

// Mark all civic notifications as read for a user
export async function markCivicNotificationsRead(userId) {
  if (!userId) return
  const { error } = await supabase
    .from('civic_notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false)
  if (error) throw error
}

// Get distinct cities from processed civic events (for filter dropdown)
export async function getCivicCities() {
  const { data, error } = await supabase
    .from('civic_events')
    .select('city, state')
    .order('city', { ascending: true })

  if (error) throw error
  if (!data) return []

  const seen = new Set()
  return data
    .filter(r => {
      const key = r.city
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .map(r => ({ city: r.city, state: r.state }))
}
